/**
 * Stage 3 — Hear the Letter.
 *
 * 5 rounds of sound-recognition. Each round:
 *   1. Fetch an adaptive quiz from /api/ai/quiz.
 *   2. Auto-play the target audio.
 *   3. Show 4 big letter buttons (target + 3 LLM-selected distractors).
 *   4. Kid taps. Correct → green bounce; wrong → gentle shake, highlight
 *      the correct answer, log the error for future adaptive quizzes.
 *
 * Scoring: 5/5 = 3 stars, 4/5 = 2 stars, 3/5 = 1 star, else 0.
 */

import { useEffect, useState } from 'preact/hooks';
import { useLocation, useRoute } from 'preact-iso';
import { api } from '../lib/api';
import { playUrl, unlockAudio } from '../lib/audio';
import { currentUser } from '../state/user';
import { Spinner } from '../components/Spinner';
import { Celebration } from '../components/Celebration';
import { NavButton } from '../components/NavButton';
import { mapState, refreshMap } from '../state/content';
import type { QuizResponse } from '@huroof/shared';

const ROUNDS = 5;

interface RoundResult {
  correct: boolean;
  expected: string;
  actual: string;
}

export function StageHear() {
  const { route } = useLocation();
  const { params } = useRoute();
  const letterId = Number(params.id);

  const [quiz, setQuiz] = useState<QuizResponse | null>(null);
  const [round, setRound] = useState(0);
  const [results, setResults] = useState<RoundResult[]>([]);
  const [picked, setPicked] = useState<string | null>(null);
  const [showHint, setShowHint] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Shuffle of target + distractors, fixed per round.
  const [choices, setChoices] = useState<Array<{ letter: string; isTarget: boolean }>>([]);

  /**
   * After 2 consecutive wrong rounds we pulse-glow the correct answer
   * for a short window before the child picks. Gentle scaffolding so a
   * frustrated child doesn't lose all 5 stars in a row.
   */
  function shouldShowHint(prev: RoundResult[]): boolean {
    if (prev.length < 2) return false;
    const last = prev[prev.length - 1];
    const prior = prev[prev.length - 2];
    return !!last && !!prior && !last.correct && !prior.correct;
  }

  useEffect(() => {
    unlockAudio();
    void loadRound();
  }, [round]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Pick the target letter for a given round.
   *
   * Round 0: always the current letter (the one the child is learning).
   * Round 1-4: mix of the current letter and mastered letters. This keeps
   * the stage interesting — 5 rounds of "identify the same letter you've
   * been hearing for 3 minutes" is boring. Including a recently-mastered
   * letter as target every other round reinforces prior learning.
   */
  function pickTargetForRound(roundIdx: number): number {
    if (roundIdx === 0) return letterId;
    const mastered = (mapState.value?.islands ?? [])
      .filter((i) => i.stars > 0 && i.letter_id !== letterId)
      .map((i) => i.letter_id);
    if (mastered.length === 0) return letterId;
    // Alternate: even round → current letter, odd round → random mastered.
    if (roundIdx % 2 === 0) return letterId;
    return mastered[Math.floor(Math.random() * mastered.length)]!;
  }

  async function loadRound() {
    if (round >= ROUNDS) {
      void finish();
      return;
    }
    try {
      // Pick a target letter — may be the current letter OR a mastered one.
      const targetId = pickTargetForRound(round);

      // Generate a fresh nonce per round so the Worker's KV cache returns a
      // DIFFERENT quiz each time. Without this the 5 rounds would all ask
      // the same question (same letterId, stage, user bucket → same cache).
      const nonce = `${Date.now().toString(36)}-${round}`;
      const resp = await api<{ ok: true; quiz: QuizResponse }>(
        '/api/ai/quiz',
        {
          method: 'POST',
          body: {
            letterId: targetId,
            stage: 3,
            stageType: 'sound_recognition',
            nonce,
          },
        },
      );
      setQuiz(resp.quiz);
      const cs = [
        { letter: resp.quiz.target.letter, isTarget: true },
        ...resp.quiz.distractors.map((d) => ({ letter: d.letter, isTarget: false })),
      ].sort(() => Math.random() - 0.5);
      setChoices(cs);
      setPicked(null);

      // If the child has lost the last 2 rounds in a row, pulse the
      // correct answer for ~2.5 s as a gentle hint. Fades out before
      // they're expected to pick so it doesn't give the answer away
      // entirely — just teaches them what they missed last time.
      const hint = shouldShowHint(results);
      setShowHint(hint);
      if (hint) setTimeout(() => setShowHint(false), 2500);

      // Auto-play the letter SOUND (not name) after a short delay — this
      // is the Hear stage, the whole point is recognition-by-sound.
      const soundUrl = `/assets/audio/letters/${targetId}_isolated.mp3`;
      setTimeout(() => void playUrl(soundUrl), 300);
    } catch (e) {
      setErr(String(e));
    }
  }

  async function pick(letterCh: string) {
    if (picked) return;
    setPicked(letterCh);
    const correct = choices.find((c) => c.letter === letterCh)?.isTarget ?? false;
    const expected = quiz?.target.letter ?? '';
    const result: RoundResult = { correct, expected, actual: letterCh };
    setResults((prev) => [...prev, result]);

    // Tactile feedback: short tick on correct, gentler double-bump on wrong.
    try {
      navigator.vibrate?.(correct ? 25 : [15, 30, 15]);
    } catch {
      /* unsupported */
    }

    // Small delay so the feedback animation plays before we advance.
    setTimeout(() => setRound((r) => r + 1), 1000);
  }

  async function finish() {
    const correctCount = results.filter((r) => r.correct).length;
    const stars = correctCount >= 5 ? 3 : correctCount >= 4 ? 2 : correctCount >= 3 ? 1 : 0;

    if (currentUser.value) {
      try {
        await api('/api/progress', {
          method: 'POST',
          body: {
            letterId,
            stage: 3,
            stars,
            attempts: ROUNDS,
            errors: results
              .filter((r) => !r.correct)
              .map((r) => ({ expected: r.expected, actual: r.actual })),
          },
        });
        // Invalidate the cached map so returning to it shows the new stars.
        void refreshMap();
      } catch {
        // Non-fatal.
      }
    }

    // Brief celebration then back to island.
    setTimeout(() => route(`/island/${letterId}`), 1600);
  }

  if (err) return <ErrorState message={err} />;

  if (round >= ROUNDS) {
    const correctCount = results.filter((r) => r.correct).length;
    const stars = correctCount >= 5 ? 3 : correctCount >= 4 ? 2 : correctCount >= 3 ? 1 : 0;
    return <Celebration stars={stars} scoreText={`${correctCount}/${ROUNDS}`} />;
  }

  if (!quiz) {
    return (
      <div class="flex-1 grid place-items-center">
        <Spinner label="جَارٍ التَّحْضِيرُ…" labelEn="Getting a question ready…" />
      </div>
    );
  }

  return (
    <div class="flex-1 flex flex-col items-center justify-between px-4 py-6 pb-10 max-w-md mx-auto w-full">
      <header class="w-full flex items-center justify-between">
        <NavButton ar="← إِنْهَاءٌ" en="Exit" onClick={() => route(`/island/${letterId}`)} />
        <div class="flex gap-1">
          {Array.from({ length: ROUNDS }).map((_, i) => (
            <span
              key={i}
              class={`h-2 w-8 rounded-full ${
                i < round
                  ? results[i]?.correct
                    ? 'bg-leaf'
                    : 'bg-berry'
                  : i === round
                    ? 'bg-coral'
                    : 'bg-ink/15'
              }`}
            />
          ))}
        </div>
      </header>

      <div class="flex-1 flex flex-col items-center justify-center gap-6 w-full">
        <div class="text-center">
          <p class="text-base font-display text-ink/60">اِسْمَعْ ثُمَّ اِخْتَرْ</p>
          <p class="text-[10px] text-ink/40" dir="ltr">Listen, then choose</p>
        </div>

        <button
          type="button"
          onClick={() =>
            void playUrl(`/assets/audio/letters/${letterId}_isolated.mp3`)
          }
          class="w-32 h-32 rounded-full bg-ocean text-white text-6xl shadow-xl shadow-ocean/30 active:scale-95"
          aria-label="Replay sound"
        >
          🔊
        </button>

        <div class="grid grid-cols-2 gap-3 w-full mt-4">
          {choices.map((c) => {
            const isPicked = picked === c.letter;
            const isTarget = c.isTarget;
            const showAsCorrect = picked !== null && isTarget;
            const showAsWrong = isPicked && !isTarget;
            const showAsHint = showHint && picked === null && isTarget;

            return (
              <button
                key={c.letter}
                type="button"
                disabled={picked !== null}
                onClick={() => pick(c.letter)}
                class={`aspect-square rounded-3xl font-display text-[80px] shadow-lg
                        transition-all
                        ${
                          showAsCorrect
                            ? 'bg-leaf text-white animate-bounce'
                            : showAsWrong
                              ? 'bg-berry text-white animate-shake'
                              : showAsHint
                                ? 'bg-white text-coral animate-hint-glow ring-4 ring-leaf/40'
                                : 'bg-white text-coral active:scale-95'
                        }`}
              >
                {c.letter}
              </button>
            );
          })}
        </div>

        {showHint && picked === null && (
          <p class="text-xs text-leaf mt-2 font-display">
            هٰذَا هُوَ الحَرْفُ
            <span class="ms-2 opacity-60" dir="ltr">
              — this one's the answer
            </span>
          </p>
        )}
      </div>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div class="flex-1 grid place-items-center p-6 text-center">
      <div>
        <p class="text-4xl mb-4">🐻</p>
        <p class="text-2xl font-display">حَاوِلْ مَرَّةً أُخْرَى!</p>
        <p class="text-xs text-ink/40 mt-3" dir="ltr">
          {message}
        </p>
      </div>
    </div>
  );
}
